import './GlobalSearchInput.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { MdClose } from 'react-icons/md';
import { IHistorySearchItem } from '@models/search/IHistorySearchItem';
import Search from '@atoms/Search';
import { RouteNames } from '@/router/router.types';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useActions } from '@hooks/useActions';
import { ConfigService } from '@/configuration/services/configService';
import Switch from '@atoms/Switch';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { SearchService } from '@services/SearchService';
import Preloader from '@atoms/Preloader';
import List from '@atoms/Menu/List';
import Button from '@atoms/Button';
import Tooltip from '@atoms/Tooltip';
import Tag from '@atoms/Tag';
import { MdAISparkles } from '@icons/react-icons-ext';
import { useLocalStorage } from 'usehooks-ts';
import { useAnalytics } from '@/analytics';

interface IGlobalSearchInputProps {
    /** Включить/отключить функцию истории поиска */
    enableSearchHistory?: boolean;
}

const GlobalSearchInput: React.FC<IGlobalSearchInputProps> = ({ enableSearchHistory = true }) => {
    const { analytics } = useAnalytics();

    const location = useLocation();
    const navigate = useNavigate();
    const { searchDocumentsGlobal, setEditingSearchText, setUseAISearch, interruptSearchDocumentsGlobal } =
        useActions();
    const isSearchLoading = useTypedSelector((state) => state.search.isGlobalSearchLoading);
    const inputSearchText = useTypedSelector((state) => state.search.searchText);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get('text') ?? '';

    const [lastSearchString, setLastSearchString] = useState<string>();

    // История поиска
    const [inputFocused, setInputFocused] = useState<boolean>(false);
    const [showHistorySearchItems, setShowHistorySearchItems] = useState<boolean>(false);
    const [loadingHistorySearchItems, setLoadingHistorySearchItems] = useState<boolean>(false);
    const [historySearchItems, setHistorySearchItems] = useState<IHistorySearchItem[]>([]);

    const config = ConfigService.get();
    const showAISearch = config.application.AI?.showAISearch ?? false;
    const useAISearch = useTypedSelector((state) => state.search.useAISearch);

    const onKeyUp = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        const searchString = e.currentTarget.value.trim();
        if (e.key === 'Enter') {
            if (searchString) {
                await doSearchByString(searchString);
            }
            // Скрыть историю поиска при нажатии Enter
            if (enableSearchHistory) {
                setShowHistorySearchItems(false);
            }
        } else {
            setEditingSearchText(searchString);
        }
    };

    const onSearchIconClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        await doSearchByString(inputSearchText);
        if (enableSearchHistory) {
            setShowHistorySearchItems(false);
        }
    };

    useEffect(() => {
        const initSearch = searchParams.get('text');
        if (!lastSearchString && initSearch) {
            setLastSearchString(initSearch);
        }
    }, []);

    const doSearchByString = useCallback(
        async (searchString: string) => {
            setLastSearchString(searchString);

            if (searchString?.length) {
                analytics.trackEvent({
                    category: 'GlobalSearch',
                    action: 'Выполнение глобального поиска',
                    name: 'global-search--search',
                    customDimensions: [{ id: 201, value: searchString }],
                });
                analytics.deleteCustomDimensions([201]);
            }

            if (location.pathname !== RouteNames.SEARCH) {
                navigate({
                    pathname: RouteNames.SEARCH,
                    search: searchString?.length ? `text=${searchString}` : undefined,
                });
            } else {
                if (searchString?.length) {
                    setSearchParams({ text: searchString });
                    const searchData = { text: searchString, requireTotalCount: true };
                    searchDocumentsGlobal(searchData);
                }
            }
        },
        [location?.pathname, navigate, searchDocumentsGlobal, setSearchParams, analytics],
    );

    const onHistorySearchSelected = useCallback(
        async (historyItem: IHistorySearchItem) => {
            await doSearchByString(historyItem.text);
            setShowHistorySearchItems(false);
        },
        [doSearchByString],
    );

    // Обработчики для истории поиска
    const removeHistorySearchItem = useCallback(async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        e.preventDefault();

        try {
            await SearchService.removeHistorySearchItem(id);
            // Обновляем список истории после удаления
            const res = await SearchService.loadHistorySearchItems();
            setHistorySearchItems(res.data);
        } catch (error) {
            console.error('Failed to remove history item:', error);
        }
    }, []);

    const onLoadHistorySearchItems = useCallback(() => {
        if (!enableSearchHistory) return;

        if (!loadingHistorySearchItems) {
            setShowHistorySearchItems(true);
            setLoadingHistorySearchItems(true);
            SearchService.loadHistorySearchItems()
                .then((res) => {
                    setHistorySearchItems(res.data);
                })
                .finally(() => {
                    setLoadingHistorySearchItems(false);
                });
        }
    }, [enableSearchHistory, loadingHistorySearchItems]);

    const handleInputFocus = useCallback(() => {
        setInputFocused(true);
        onLoadHistorySearchItems();

        analytics.trackEvent({
            category: 'GlobalSearch',
            action: 'Фокус поля ввода глобального поиска',
            name: 'global-search-input--focus',
        });
    }, [analytics, onLoadHistorySearchItems]);

    const handleInputBlur = useCallback(() => {
        setInputFocused(false);
        setShowHistorySearchItems(false);

        analytics.trackEvent({
            category: 'GlobalSearch',
            action: 'Потеря фокуса поля ввода глобального поиска',
            name: 'global-search-input--blur',
        });
    }, [analytics]);

    // Рендер истории поиска
    const historyDropdownContent = useMemo(() => {
        if (!enableSearchHistory || !showHistorySearchItems || !inputFocused) return null;

        if (historySearchItems?.length > 0) {
            return (
                <div className="global-search__history-wrapper">
                    {loadingHistorySearchItems ? (
                        <Preloader size="m" />
                    ) : (
                        <List
                            className="dropDownHistoryItems"
                            handlerType="mouseDown"
                            list={historySearchItems.map((x) => ({
                                label: (
                                    <div className={'rf-search__historyItem'}>
                                        <div className={'rf-search__historyItemText'}>{x.text}</div>
                                        <Button
                                            size="s"
                                            buttonType="text"
                                            textColor="neutral"
                                            onMouseDown={(e) => {
                                                removeHistorySearchItem(e, x.id);
                                            }}
                                            aria-label={'Удалить из истории'}
                                            startAdornment={<MdClose size="20" />}
                                        />
                                    </div>
                                ),
                                value: x.text,
                                handler: () => {
                                    onHistorySearchSelected(x);
                                },
                            }))}
                        />
                    )}
                </div>
            );
        }

        return null;
    }, [
        enableSearchHistory,
        showHistorySearchItems,
        inputFocused,
        loadingHistorySearchItems,
        historySearchItems,
        removeHistorySearchItem,
        onHistorySearchSelected,
    ]);

    const currentUser = useTypedSelector((state) => state.auth.user);
    const [aiSwitchHintDismissed, setAiSwitchHintDismissed] = useLocalStorage<boolean>(
        `user-${currentUser.username}.aiSwitchHint`,
        false,
    );
    const aiSwitchTooltipText = config.application.AI?.aiSearchSwitchTooltip?.text ?? '';
    const aiSwitchTooltipExamples = config.application.AI?.aiSearchSwitchTooltip?.examples ?? [];

    const aiSwitchTooltipContent = !aiSwitchHintDismissed ? (
        <div className="ai-switch__tooltip-content">
            <div className="ai-switch__tooltip-title">
                AI поиск
                <Tag variant="purple" style={{ height: 'initial', padding: '0.25rem 0.75rem' }}>
                    Новое
                </Tag>
            </div>
            <div className="ai-switch__tooltip-text">{aiSwitchTooltipText}</div>
            <div className="ai-switch__tooltip-examples">
                {aiSwitchTooltipExamples.map((example, index) => (
                    <Button
                        key={index}
                        buttonType="light"
                        textColor="neutral"
                        size="s"
                        startAdornment={<MdAISparkles size="20" color="#625FF0" />}
                        className="ai-switch__tooltip-example-button"
                        onClick={() => {
                            setUseAISearch(true);
                            doSearchByString(example);
                        }}
                    >
                        {example}
                    </Button>
                ))}
            </div>
            <div className="ai-switch__tooltip-dismiss-row">
                <Button
                    buttonType="primary"
                    textColor="neutral"
                    size="s"
                    className="ai-switch__tooltip-dismiss"
                    onClick={() => {
                        setAiSwitchHintDismissed(true);
                    }}
                >
                    Понятно
                </Button>
            </div>
        </div>
    ) : null;

    return (
        <div className={clsx('global-search')}>
            <Search
                endAdornment={
                    showAISearch && (
                        <div className="global-search-switcher">
                            <Tooltip
                                className="global-search-ai-switch-tooltip"
                                openDelay={100}
                                position="bottom"
                                hidePadding={true}
                            >
                                <Switch
                                    label="AI поиск"
                                    className="global-search-ai-switch"
                                    value={useAISearch}
                                    between={false}
                                    reverse={true}
                                    disabled={isSearchLoading}
                                    onChange={(value) => {
                                        setUseAISearch(value);
                                        if (lastSearchString) {
                                            doSearchByString(lastSearchString);
                                        }
                                    }}
                                />
                                {aiSwitchTooltipContent}
                            </Tooltip>
                        </div>
                    )
                }
                onClear={() => {
                    interruptSearchDocumentsGlobal();
                    setEditingSearchText('');
                }}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={isSearchLoading}
                width={350}
                onKeyUp={onKeyUp}
                value={searchText}
                onInputKeyDown={onKeyUp}
                showDropdown={enableSearchHistory && showHistorySearchItems && inputFocused}
                dropdownContent={historyDropdownContent}
                onSearchIconClick={onSearchIconClick}
            />
        </div>
    );
};

export default GlobalSearchInput;
